import { takeLatest, put, all, call } from 'redux-saga/effects';
import { commonActions, CommonSlice } from 'app/store/common.slice';
import { axiosInstance } from 'setup/axiosInstance';
import {
  VocabularyIrregularVerb,
  DictionaryWord,
  DictionaryIrregularVerb,
  ValueOf,
  VocabularyTypeRecord,
  WordSetCategoryType,
  PageCategoryTO,
  VocabularyWordsPage,
} from 'app/types';

type RawVocabularyIrregularVerb = VocabularyIrregularVerb & {
  activeSpeach?: boolean;
  learned?: boolean;
};
type RawDictionaryWord = DictionaryWord & {
  activeSpeech?: boolean;
  activeSpeach?: boolean;
  learned?: boolean;
};
type RawDictionaryIrregularVerb = DictionaryIrregularVerb & {
  activeSpeech?: boolean;
  activeSpeach?: boolean;
  learned?: boolean;
};

const normalizeVocabularyVerb = (
  verb: RawVocabularyIrregularVerb,
): VocabularyIrregularVerb => ({
  ...verb,
  isActiveSpeech: Boolean(verb.isActiveSpeech ?? verb.activeSpeach),
  isLearned: Boolean(verb.isLearned ?? verb.learned),
});

const mapVocabularyVerbToRequest = (verb: VocabularyIrregularVerb) => ({
  ...verb,
});

const normalizeDictionaryWord = (word: RawDictionaryWord): DictionaryWord => ({
  ...word,
  isActiveSpeech: Boolean(
    word.isActiveSpeech ?? word.activeSpeech ?? word.activeSpeach,
  ),
  isLearned: Boolean(word.isLearned ?? word.learned),
});

const normalizeDictionaryVerb = (
  verb: RawDictionaryIrregularVerb,
): DictionaryIrregularVerb => ({
  ...verb,
  isActiveSpeech: Boolean(
    verb.isActiveSpeech ?? verb.activeSpeech ?? verb.activeSpeach,
  ),
  isLearned: Boolean(verb.isLearned ?? verb.learned),
});

function* getWordSetDataSaga() {
  const { data }: { data: CommonSlice['wordSetData']['data'] } =
    yield axiosInstance.get('wordset/');
  yield put(commonActions.setWordSetData(data));
}

function* getWordSetLevelBasedSaga(wordSetType: WordSetCategoryType) {
  const { data }: { data: PageCategoryTO } = yield axiosInstance.get(
    `wordset/${wordSetType}`,
  );
  yield put(commonActions.setWordSetCategories({ wordSetType, data }));
}

function* getCommonData() {
  yield all([
    call(getWordSetDataSaga),
    call(getWordSetLevelBasedSaga, 'level-based'),
    call(getWordSetLevelBasedSaga, 'most-common'),
    call(getWordSetLevelBasedSaga, 'topic-related'),
    call(getWordSetLevelBasedSaga, 'collocations'),
    call(getWordSetLevelBasedSaga, 'idioms'),
    call(getWordSetLevelBasedSaga, 'phrasal-verbs'),
    call(getWordSetLevelBasedSaga, 'irregular-verb'),
    call(getVocabularyDataSaga),
  ]);
}

function* getCategoriesSaga({
  payload: { wordSetType, categoryName },
}: ReturnType<typeof commonActions.getCategories>) {
  const { data }: { data: ValueOf<CommonSlice['categories']>['data'] } =
    yield axiosInstance.get(`/wordset/${wordSetType}/${categoryName}`);

  yield put(
    commonActions.setCategories({
      categoryName,
      wordSetType,
      data,
    }),
  );
}

function* getSubCategoryWordsSaga({
  payload: { categoryName, subcategoryName, wordSetType },
}: ReturnType<typeof commonActions.getSubCategoryWords>) {
  const {
    data,
  }: {
    data: Record<typeof subcategoryName, RawDictionaryWord[]>;
  } = yield axiosInstance.get(
    `/wordset/${wordSetType}/${categoryName}/${subcategoryName}`,
  );

  yield put(
    commonActions.setSubCategoryWords(
      (data[subcategoryName] || []).map(normalizeDictionaryWord),
    ),
  );
}

function* getIrregularVerbWordsSaga({
  payload: { levelBased },
}: ReturnType<typeof commonActions.getIrregularVerbWords>) {
  const {
    data,
  }: {
    data:
      | RawDictionaryIrregularVerb[]
      | Record<string, RawDictionaryIrregularVerb[]>;
  } = yield axiosInstance.get(`/wordset/irregular-verb/${levelBased}`);

  yield put(
    commonActions.setIrregularVerbWords({
      levelBased,
      data: (Array.isArray(data) ? data : data[levelBased] || []).map(
        normalizeDictionaryVerb,
      ),
    }),
  );
}

function* getVocabularyDataSaga() {
  try {
    const { data }: { data: VocabularyTypeRecord[] } = yield axiosInstance.get(
      `/vocabulary`,
    );
    yield put(commonActions.setVocabularyData(data));
  } catch (e) {
    console.error(e);
  }
}

const filterParam = (value?: string) =>
  !value || value === 'all' ? undefined : value;

function* vocabularyWordsGetSaga(
  action?: ReturnType<typeof commonActions.vocabularyWordsGet>,
) {
  try {
    const payload = action?.payload || {};
    const {
      append = false,
      filters = {},
      page = 0,
      size = 20,
      wordType = 'WORD',
      sortBy,
      search,
    } = payload || {};
    const endpoint =
      wordType === 'EXPRESSION'
        ? `/vocabulary/expressions/`
        : `/vocabulary/words`;
    const trimmedSearch = search?.trim();
    const { data }: { data: VocabularyWordsPage } = yield axiosInstance.get(
      endpoint,
      {
        params: {
          page,
          size,
          wordType: wordType === 'WORD' ? wordType : undefined,
          isActiveSpeech: filterParam(filters.isActiveSpeech),
          isLearned: filterParam(filters.isLearned),
          partOfSpeech: filterParam(filters.partOfSpeech),
          sortBy,
          search: trimmedSearch ? trimmedSearch : undefined,
        },
      },
    );

    yield put(
      commonActions.vocabularyWordsSet({
        append,
        content: data.content.map(normalizeDictionaryWord),
        number: data.number,
        partOfSpeechOptions: data.partOfSpeechOptions,
        size: data.size,
        totalElements: data.totalElements,
        totalPages: data.totalPages,
        wordType,
      }),
    );
  } catch (e) {
    console.error(e);
  }
}

function* vocabularyWordsDeleteSaga({
  payload: { filters, page = 0, size, wordIds, wordType = 'WORD' },
}: ReturnType<typeof commonActions.vocabularyWordsDelete>) {
  try {
    const endpoint =
      wordType === 'EXPRESSION'
        ? `/vocabulary/expressions/`
        : `/vocabulary/words`;
    yield axiosInstance.delete(endpoint, {
      data: wordIds,
      params: {
        wordType: wordType === 'WORD' ? wordType : undefined,
      },
    });
    yield call(
      vocabularyWordsGetSaga,
      commonActions.vocabularyWordsGet({ filters, page, size, wordType }),
    );
    yield call(getVocabularyDataSaga);
  } catch (e) {
    console.error(e);
    yield call(
      vocabularyWordsGetSaga,
      commonActions.vocabularyWordsGet({ filters, page, size, wordType }),
    );
  }
}

function* vocabularyWordsUpdateSaga({
  payload: { filters, page = 0, size, words, wordType = 'WORD' },
}: ReturnType<typeof commonActions.vocabularyWordsUpdate>) {
  try {
    const endpoint =
      wordType === 'EXPRESSION'
        ? `/vocabulary/expressions/`
        : `/vocabulary/words`;
    yield axiosInstance.put(endpoint, words, {
      params: {
        wordType: wordType === 'WORD' ? wordType : undefined,
      },
    });
    yield call(
      vocabularyWordsGetSaga,
      commonActions.vocabularyWordsGet({ filters, page, size, wordType }),
    );
    yield call(getVocabularyDataSaga);
  } catch (e) {
    console.error(e);
    yield call(
      vocabularyWordsGetSaga,
      commonActions.vocabularyWordsGet({ filters, page, size, wordType }),
    );
  }
}

function* vocabularyVerbsGetSaga() {
  try {
    const { data }: { data: RawVocabularyIrregularVerb[] } =
      yield axiosInstance.get(`/vocabulary/verbs/`);

    yield put(
      commonActions.vocabularyVerbsSet(data.map(normalizeVocabularyVerb)),
    );
  } catch (e) {
    console.error(e);
  }
}

function* vocabularyVerbsDeleteSaga({
  payload: { verbIds },
}: ReturnType<typeof commonActions.vocabularyVerbsDelete>) {
  try {
    yield axiosInstance.delete(`/vocabulary/verbs/`, {
      data: verbIds,
    });
    yield call(vocabularyVerbsGetSaga);
    yield call(getVocabularyDataSaga);
  } catch (e) {
    console.error(e);
    yield call(vocabularyVerbsGetSaga);
  }
}

function* vocabularyVerbsUpdateSaga({
  payload: { verbs },
}: ReturnType<typeof commonActions.vocabularyVerbsUpdate>) {
  try {
    yield axiosInstance.put(
      `/vocabulary/verbs/`,
      verbs.map(mapVocabularyVerbToRequest),
    );
    yield call(vocabularyVerbsGetSaga);
    yield call(getVocabularyDataSaga);
  } catch (e) {
    console.error(e);
    yield call(vocabularyVerbsGetSaga);
  }
}

export default function* commonSaga() {
  yield takeLatest(commonActions.getCommonData, getCommonData);
  yield takeLatest(commonActions.getCategories, getCategoriesSaga);
  yield takeLatest(commonActions.getSubCategoryWords, getSubCategoryWordsSaga);
  yield takeLatest(
    commonActions.getIrregularVerbWords,
    getIrregularVerbWordsSaga,
  );
  yield takeLatest(commonActions.getVocabularyData, getVocabularyDataSaga);
  yield takeLatest(commonActions.vocabularyWordsGet, vocabularyWordsGetSaga);
  yield takeLatest(
    commonActions.vocabularyWordsDelete,
    vocabularyWordsDeleteSaga,
  );
  yield takeLatest(
    commonActions.vocabularyWordsUpdate,
    vocabularyWordsUpdateSaga,
  );
  yield takeLatest(commonActions.vocabularyVerbsGet, vocabularyVerbsGetSaga);
  yield takeLatest(
    commonActions.vocabularyVerbsDelete,
    vocabularyVerbsDeleteSaga,
  );
  yield takeLatest(
    commonActions.vocabularyVerbsUpdate,
    vocabularyVerbsUpdateSaga,
  );
}
